import type { Contact, ContactPhone, ContactSocialNetwork } from '../types/contact.types'

export type ContactState = 'new' | 'modified' | 'deleted' | null

export const STATE_SHADOW_STYLE: Record<string, string> = {
  new: 'shadow-[inset_4px_0_0_0_var(--color-primary-400)]',
  modified: 'shadow-[inset_4px_0_0_0_var(--color-amber-400)]',
  deleted: 'shadow-[inset_4px_0_0_0_var(--color-red-400)]',
}

export function getContactState(
  contact: Contact,
  pendingAdds: Contact[],
  pendingEdits: Map<string, Contact>,
  pendingDeletes: Set<string>,
): ContactState {
  if (contact.id != null && pendingDeletes.has(contact.id)) return 'deleted'
  if (pendingAdds.some((c) => c.id === contact.id)) return 'new'
  if (contact.id != null && pendingEdits.has(contact.id)) return 'modified'
  return null
}

export function formatPhoneForGrid(phone: ContactPhone, categoryLabelMap?: Map<number, string>): string {
  let result = categoryLabelMap?.get(phone.category) ?? String(phone.category)
  if (phone.areaCode) {
    result += ` ${phone.areaCode}-${phone.number}`
  } else {
    result += ` ${phone.number}`
  }
  if (phone.extension) result += ` ext ${phone.extension}`
  return result
}

export function formatSocialForGrid(
  sn: ContactSocialNetwork,
  socialNetworkLabelMap: Map<string, string> | undefined,
  notEnabledLabel: string,
): string {
  const url = sn.url.length > 40 ? `${sn.url.substring(0, 37)}...` : sn.url
  // A map that exists but doesn't contain this network's id means the company's own
  // lookup search (now company-scoped, see contactAdapter.ts) never returned it — the
  // network isn't enabled for the active company, so its raw GUID would otherwise leak
  // into the grid instead of a name. No map at all (no company context) falls back to
  // the id, same as before — there's nothing to say it's "not enabled" against.
  const label = socialNetworkLabelMap
    ? socialNetworkLabelMap.get(sn.socialNetworkTypeId) ?? notEnabledLabel
    : sn.socialNetworkTypeId
  return `${label}: ${url}`
}

export const DEFAULT_PAGE_SIZE = 5
